/**
 * Le azioni del registro piscine, in un posto solo.
 *
 * Sul foglio Padma che il cliente compila ogni mattina le azioni sono la sostanza della riga:
 * pulizia filtri, pulizia skimmer, carico cloro, pulizia piscina e reintegro d'acqua. Finche
 * vivevano nel testo libero nessuno poteva contarle; qui diventano spunte, e il reintegro porta
 * con se i litri, perche il condominio chiede quanta acqua e stata messa nella vasca.
 *
 * Questo elenco e l'unico: modulo di rilevazione, storico, filtri e stampa leggono da qui, come
 * le unita di misura dei materiali leggono da `material-units.ts`.
 */
export const POOL_ACTIONS = [
  { key: "filterCleaning", label: "Pulizia filtri", short: "Filtri" },
  { key: "skimmerCleaning", label: "Pulizia skimmer", short: "Skimmer" },
  { key: "chlorineLoad", label: "Carico cloro", short: "Carico cloro" },
  { key: "poolCleaning", label: "Pulizia piscina", short: "Piscina" },
  { key: "waterRefill", label: "Reintegro d'acqua", short: "Reintegro" },
] as const;

export type PoolActionKey = (typeof POOL_ACTIONS)[number]["key"];

export type PoolActionFlags = Record<PoolActionKey, boolean>;

/** Le chiavi delle azioni, nella forma che zod si aspetta per un elenco chiuso. */
export const POOL_ACTION_KEYS = POOL_ACTIONS.map((azione) => azione.key) as [PoolActionKey, ...PoolActionKey[]];

/** Il massimo di litri accettato per un reintegro: oltre, e un errore di scrittura. */
export const MAX_REFILL_LITERS = 100_000;

/** Le azioni segnate su una rilevazione, scritte come le direbbe una persona. */
export function poolActionLabels(reading: Partial<PoolActionFlags>): string[] {
  return POOL_ACTIONS.filter((azione) => reading[azione.key]).map((azione) => azione.label);
}

/** Le sigle per la tabella, dove lo spazio e poco: "Filtri, Skimmer, Reintegro". */
export function poolActionShortLabels(reading: Partial<PoolActionFlags>): string[] {
  return POOL_ACTIONS.filter((azione) => reading[azione.key]).map((azione) => azione.short);
}

/**
 * I litri reintegrati in un periodo.
 *
 * Le righe senza reintegro e quelle in cui il collaboratore ha segnato la spunta ma non la
 * quantita valgono zero: il totale dice quanta acqua e stata misurata, non quante volte.
 */
export function totalRefillLiters(readings: Array<{ waterRefillLiters?: number | null }>): number {
  return readings.reduce((totale, lettura) => totale + (lettura.waterRefillLiters ?? 0), 0);
}

/** I litri scritti come su un documento: "1.500 l", con la virgola solo se serve. */
export function formatLiters(liters: number): string {
  return `${liters.toLocaleString("it-IT", { maximumFractionDigits: 1 })} l`;
}
